// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { StyledButton } from "@insite/mobius/Clickable";
import DataTablePresentationPropsDefault from "@insite/mobius/DataTable/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { DataTableCssOverrides } from "@vanmeter/MobiusOverrides/DataTable/DataTable";
import { fontConfig } from "@vanmeter/XDL/constants";
import DescendingSort from "@vanmeter/XDL/Icons/IconCaretDown";
import AscendingSort from "@vanmeter/XDL/Icons/IconCaretUp";
import Sort from "@vanmeter/XDL/Icons/IconSort";
import { xBaseTheme } from "@vanmeter/XDL/types";
import { color, xScreen } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

const stickyMode = () => css`
    ${xScreen("lte", "sm")} {
        position: sticky;
        left: 0;
        z-index: 2;
        background-clip: padding-box;
        background: inherit;
        > ${TypographyStyle} {
            display: block;
            max-width: 140px;
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }
`;

export default {
    defaultProps: {
        ...DataTablePresentationPropsDefault,
        mergeCss: true,
        cssOverrides: {
            header: css`
                background: ${color("common.ui7")};
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
            `,
            head: css`
                background: ${color("common.ui7")};
                th:first-child {
                    ${stickyMode()}
                }
            `,
            cell: css`
                padding: 8px;
                border-bottom: 0;
                height: initial;
                line-height: normal;
                ${StyledButton} > ${TypographyStyle} {
                    font-size: ${fontConfig.smallSingleLine.size}px;
                    line-height: ${fontConfig.smallSingleLine.lineHeight};
                }
                &:first-child {
                    ${stickyMode()}
                }
            `,
            row: css`
                background: ${color("common.white")};
            `,
            evenRow: css`
                background: ${color("common.ui2")};
            `,
        } as DataTableCssOverrides,
        headerTypographyProps: {
            mergeCss: true,
            ...fontConfig.smallSingleLine,
            weight: fontConfig.weight.bold,
            color: "common.white",
            css: css`
                letter-spacing: 0;
            `,
        },
        cellTypographyProps: {
            mergeCss: true,
            ...fontConfig.smallSingleLine,
            weight: fontConfig.weight.regular,
            css: css`
                letter-spacing: 0;
            `,
        },
        sortClickableProps: {
            css: css`
                width: 100%;
                height: 100%;
                gap: 4px;
                &:hover {
                    ${TypographyStyle}, ${IconWrapper} {
                        color: ${color("common.ui17")};
                    }
                }
            `,
        },
        sortIconSources: {
            sortable: Sort,
            ascending: AscendingSort,
            descending: DescendingSort,
        },
        sortIconProps: {
            width: 9,
            height: 13,
            color: "common.white",
            css: css`
                margin-left: auto;
            `,
        },
        css: css`
            td[class^="DataTableCellBase"] {
                & > ${TypographyStyle}:empty:after {
                    content: "- -";
                }
            }
        `,
    },
} as xBaseTheme["dataTable"];
